import React, { useState, useRef, useEffect } from 'react';
import { MapContainer, TileLayer, FeatureGroup, Polyline, Marker, Popup } from 'react-leaflet';
import { EditControl } from 'react-leaflet-draw';
import { X, Check, Plus, Edit, Trash2, GitBranch, Save } from 'lucide-react';
import L from 'leaflet';

// Configurar ícones do Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.8.0/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.8.0/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.8.0/images/marker-shadow.png',
});

const GerenciadorTrechos = ({ onTrechosAtualizados, onFechar, titulo, trechosIniciais = [] }) => {
  const [trechos, setTrechos] = useState(trechosIniciais);
  const [trechoAtivo, setTrechoAtivo] = useState(null);
  const [modoEdicao, setModoEdicao] = useState('visualizar'); // 'visualizar', 'novo-trecho', 'nova-derivacao'
  const [pontoSelecionado, setPontoSelecionado] = useState(null);
  const [nomeNovoTrecho, setNomeNovoTrecho] = useState('');
  const [nomeNovaDerivacao, setNomeNovaDerivacao] = useState('');
  const featureGroupRef = useRef();
  
  // Coordenadas de Conceição do Castelo - ES
  const centroConceicaoCastelo = [-20.359292, -41.247282];

  useEffect(() => {
    // Renderizar trechos existentes no mapa
    if (featureGroupRef.current && trechos.length > 0) {
      featureGroupRef.current.clearLayers();
      
      trechos.forEach(trecho => {
        // Desenhar trecho principal
        const polyline = L.polyline(trecho.pontos, { 
          color: '#007bff', 
          weight: 4,
          opacity: 0.8
        });
        featureGroupRef.current.addLayer(polyline);
        
        // Desenhar derivações
        if (trecho.derivacoes) {
          trecho.derivacoes.forEach(derivacao => {
            const derivacaoPolyline = L.polyline(derivacao.pontos, { 
              color: '#28a745', 
              weight: 3,
              opacity: 0.7,
              dashArray: '5, 5'
            });
            featureGroupRef.current.addLayer(derivacaoPolyline);
          });
        }
      });
    }
  }, [trechos]);

  const handleCreated = (e) => {
    const { layer } = e;
    
    if (layer instanceof L.Polyline) {
      const coordenadas = layer.getLatLngs().map(latlng => [latlng.lat, latlng.lng]);
      
      if (modoEdicao === 'novo-trecho') {
        adicionarNovoTrecho(coordenadas);
      } else if (modoEdicao === 'nova-derivacao' && trechoAtivo && pontoSelecionado !== null) {
        adicionarNovaDerivacao(coordenadas);
      }
    }
  };

  const adicionarNovoTrecho = (pontos) => {
    if (!nomeNovoTrecho.trim()) {
      alert('Por favor, insira um nome para o trecho');
      return;
    }

    const novoTrecho = {
      id: Date.now(),
      nome: nomeNovoTrecho.trim(),
      tipo: 'principal',
      pontos: pontos,
      derivacoes: []
    };

    setTrechos(prev => [...prev, novoTrecho]);
    setNomeNovoTrecho('');
    setModoEdicao('visualizar');
  };

  const adicionarNovaDerivacao = (pontos) => {
    if (!nomeNovaDerivacao.trim()) {
      alert('Por favor, insira um nome para a derivação');
      return;
    }

    const novaDerivacao = {
      id: Date.now(),
      nome: nomeNovaDerivacao.trim(),
      pontoOrigem: pontoSelecionado,
      pontos: pontos
    };

    setTrechos(prev => prev.map(trecho => 
      trecho.id === trechoAtivo.id 
        ? { ...trecho, derivacoes: [...(trecho.derivacoes || []), novaDerivacao] }
        : trecho
    ));

    setNomeNovaDerivacao('');
    setModoEdicao('visualizar');
    setTrechoAtivo(null);
    setPontoSelecionado(null);
  };

  const excluirTrecho = (trechoId) => {
    if (window.confirm('Tem certeza que deseja excluir este trecho e todas suas derivações?')) {
      setTrechos(prev => prev.filter(t => t.id !== trechoId));
    }
  };

  const excluirDerivacao = (trechoId, derivacaoId) => {
    if (window.confirm('Tem certeza que deseja excluir esta derivação?')) {
      setTrechos(prev => prev.map(trecho => 
        trecho.id === trechoId 
          ? { ...trecho, derivacoes: trecho.derivacoes.filter(d => d.id !== derivacaoId) }
          : trecho
      ));
    }
  };

  const iniciarNovoTrecho = () => {
    setModoEdicao('novo-trecho');
    setNomeNovoTrecho(`Trecho ${trechos.length + 1}`);
  };

  const iniciarNovaDerivacao = (trecho) => {
    setTrechoAtivo(trecho);
    setModoEdicao('nova-derivacao');
    setNomeNovaDerivacao(`Derivação ${(trecho.derivacoes?.length || 0) + 1}`);
  };

  const selecionarPontoParaDerivacao = (trechoId, pontoIndex) => {
    setPontoSelecionado(pontoIndex);
  };

  const confirmarTrechos = () => {
    if (trechos.length === 0) {
      alert('É necessário adicionar pelo menos um trecho');
      return;
    }
    onTrechosAtualizados(trechos);
  };

  const cancelarEdicao = () => {
    setModoEdicao('visualizar');
    setTrechoAtivo(null);
    setPontoSelecionado(null);
    setNomeNovoTrecho('');
    setNomeNovaDerivacao('');
  };

  return (
    <div className="modal-overlay">
      <div className="modal" style={{ maxWidth: '1200px', width: '95%', height: '90vh' }}>
        <div className="modal-header">
          <h3 className="modal-title">{titulo}</h3>
          <button className="close-button" onClick={onFechar}>
            <X size={20} />
          </button>
        </div>

        <div style={{ display: 'flex', height: 'calc(100% - 80px)', gap: '20px' }}>
          {/* Painel lateral de controle */}
          <div style={{ width: '300px', backgroundColor: '#f8f9fa', borderRadius: '8px', padding: '15px', overflowY: 'auto' }}>
            <div style={{ marginBottom: '20px' }}>
              <h4 style={{ marginBottom: '10px' }}>Controles</h4>
              
              {modoEdicao === 'visualizar' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  <button className="button" onClick={iniciarNovoTrecho}>
                    <Plus size={16} />
                    Novo Trecho
                  </button>
                </div>
              )}

              {modoEdicao === 'novo-trecho' && (
                <div>
                  <div className="form-group" style={{ marginBottom: '10px' }}>
                    <label>Nome do Trecho:</label>
                    <input
                      type="text"
                      value={nomeNovoTrecho}
                      onChange={(e) => setNomeNovoTrecho(e.target.value)}
                      placeholder="Ex: Rua Principal"
                    />
                  </div>
                  <p style={{ fontSize: '12px', color: '#666', marginBottom: '10px' }}>
                    Desenhe o trecho no mapa clicando nos pontos desejados
                  </p>
                  <button className="button secondary" onClick={cancelarEdicao}>
                    Cancelar
                  </button>
                </div>
              )}

              {modoEdicao === 'nova-derivacao' && (
                <div>
                  <div className="form-group" style={{ marginBottom: '10px' }}>
                    <label>Nome da Derivação:</label>
                    <input
                      type="text"
                      value={nomeNovaDerivacao}
                      onChange={(e) => setNomeNovaDerivacao(e.target.value)}
                      placeholder="Ex: Acesso à Escola"
                    />
                  </div>
                  <p style={{ fontSize: '12px', color: '#666', marginBottom: '10px' }}>
                    {pontoSelecionado !== null 
                      ? 'Agora desenhe a derivação no mapa'
                      : 'Primeiro clique em um ponto do trecho principal'
                    }
                  </p>
                  <button className="button secondary" onClick={cancelarEdicao}>
                    Cancelar
                  </button>
                </div>
              )}
            </div>

            {/* Lista de trechos */}
            <div>
              <h4 style={{ marginBottom: '10px' }}>Trechos ({trechos.length})</h4>
              
              {trechos.length === 0 ? (
                <p style={{ fontSize: '14px', color: '#666', textAlign: 'center', padding: '20px' }}>
                  Nenhum trecho adicionado
                </p>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  {trechos.map(trecho => (
                    <div key={trecho.id} style={{
                      padding: '10px',
                      backgroundColor: 'white',
                      borderRadius: '6px',
                      border: '1px solid #dee2e6'
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                        <strong style={{ fontSize: '14px' }}>{trecho.nome}</strong>
                        <button
                          className="button danger"
                          onClick={() => excluirTrecho(trecho.id)}
                          style={{ padding: '4px 8px', fontSize: '12px' }}
                        >
                          <Trash2 size={12} />
                        </button>
                      </div>
                      
                      <p style={{ fontSize: '12px', color: '#666', marginBottom: '8px' }}>
                        {trecho.pontos.length} pontos
                      </p>

                      <button
                        className="button"
                        onClick={() => iniciarNovaDerivacao(trecho)}
                        style={{ padding: '4px 8px', fontSize: '12px', width: '100%' }}
                        disabled={modoEdicao !== 'visualizar'}
                      >
                        <GitBranch size={12} />
                        Adicionar Derivação
                      </button>

                      {/* Lista de derivações */}
                      {trecho.derivacoes && trecho.derivacoes.length > 0 && (
                        <div style={{ marginTop: '8px', paddingLeft: '10px' }}>
                          <p style={{ fontSize: '12px', fontWeight: 'bold', marginBottom: '5px' }}>
                            Derivações:
                          </p>
                          {trecho.derivacoes.map(derivacao => (
                            <div key={derivacao.id} style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              padding: '4px 8px',
                              backgroundColor: '#e9ecef',
                              borderRadius: '4px',
                              marginBottom: '4px'
                            }}>
                              <span style={{ fontSize: '12px' }}>{derivacao.nome}</span>
                              <button
                                className="button danger"
                                onClick={() => excluirDerivacao(trecho.id, derivacao.id)}
                                style={{ padding: '2px 6px', fontSize: '10px' }}
                              >
                                <Trash2 size={10} />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Mapa */}
          <div style={{ flex: 1 }}>
            <MapContainer
              center={centroConceicaoCastelo}
              zoom={14}
              style={{ height: '100%', width: '100%', borderRadius: '8px' }}
            >
              <TileLayer
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              />
              
              {/* Renderizar trechos existentes */}
              {trechos.map(trecho => (
                <React.Fragment key={trecho.id}>
                  {/* Trecho principal */}
                  <Polyline
                    positions={trecho.pontos}
                    color="#007bff"
                    weight={4}
                    opacity={0.8}
                  />
                  
                  {/* Pontos clicáveis para derivações */}
                  {modoEdicao === 'nova-derivacao' && trechoAtivo?.id === trecho.id && (
                    trecho.pontos.map((ponto, index) => (
                      <Marker
                        key={index}
                        position={ponto}
                        eventHandlers={{
                          click: () => selecionarPontoParaDerivacao(trecho.id, index)
                        }}
                      >
                        <Popup>
                          <div>
                            <p>Ponto {index + 1} do {trecho.nome}</p>
                            <button 
                              className="button"
                              onClick={() => selecionarPontoParaDerivacao(trecho.id, index)}
                              style={{ fontSize: '12px', padding: '4px 8px' }}
                            >
                              Usar como origem da derivação
                            </button>
                          </div>
                        </Popup>
                      </Marker>
                    ))
                  )}
                  
                  {/* Derivações */}
                  {trecho.derivacoes && trecho.derivacoes.map(derivacao => (
                    <Polyline
                      key={derivacao.id}
                      positions={derivacao.pontos}
                      color="#28a745"
                      weight={3}
                      opacity={0.7}
                      dashArray="5, 5"
                    />
                  ))}
                </React.Fragment>
              ))}
              
              {/* Controles de desenho */}
              <FeatureGroup ref={featureGroupRef}>
                <EditControl
                  position="topright"
                  onCreated={handleCreated}
                  draw={{
                    rectangle: false,
                    polygon: false,
                    circle: false,
                    marker: false,
                    circlemarker: false,
                    polyline: modoEdicao === 'novo-trecho' || modoEdicao === 'nova-derivacao' ? {
                      shapeOptions: {
                        color: modoEdicao === 'novo-trecho' ? '#007bff' : '#28a745',
                        weight: modoEdicao === 'novo-trecho' ? 4 : 3,
                        opacity: 0.8,
                        dashArray: modoEdicao === 'nova-derivacao' ? '5, 5' : null
                      }
                    } : false
                  }}
                  edit={false}
                />
              </FeatureGroup>
            </MapContainer>
          </div>
        </div>

        {/* Botões de ação */}
        <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '15px' }}>
          <button className="button secondary" onClick={onFechar}>
            Cancelar
          </button>
          <button 
            className="button" 
            onClick={confirmarTrechos}
            disabled={trechos.length === 0}
          >
            <Save size={16} />
            Salvar Trechos ({trechos.length})
          </button>
        </div>
      </div>
    </div>
  );
};

export default GerenciadorTrechos;